import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import {
  collection, getDoc, getDocs, doc, setDoc,
  query, where, serverTimestamp
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton, Button,
  TextField, Chip, Divider, Alert, CircularProgress,
  LinearProgress, Checkbox, FormControlLabel, Tooltip,
  List, ListItem, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { callClaude } from '../utils/claudeProxy';

// ── Hämta text från PDF via Claude ────────────────────────────────
async function fetchDocumentText(fileUrl) {
  const res = await fetch(fileUrl);
  const blob = await res.blob();
  const base64 = await new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(blob);
  });
  const json = await callClaude({
    model: 'claude-haiku-4-5-20251001',
    max_tokens: 4000,
    messages: [{
      role: 'user',
      content: [
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
        { type: 'text', text: 'Extrahera all text från detta dokument och returnera det som ren text.' }
      ]
    }]
  });
  return json.content?.[0]?.text || '';
}

// ── Generera förväntningsmatris med AI ────────────────────────────
async function generateMatrix({ docTexts, freeText, expectations, inheritedMatrix, scope }) {
  const sections = [];
  if (inheritedMatrix?.length) {
    sections.push(`**Ärvda förväntningar från ${scope === 'area' ? 'VD' : 'överordnad'}:**\n` +
      inheritedMatrix.filter(m => m.active).map(m => `- ${m.text}`).join('\n'));
  }
  if (docTexts?.length) {
    sections.push(`**Dokumentinnehåll:**\n${docTexts.join('\n\n---\n\n').slice(0, 8000)}`);
  }
  if (freeText) sections.push(`**Fritext om förväntningar:**\n${freeText}`);
  if (expectations?.length) {
    sections.push(`**Strukturerade förväntningar:**\n${expectations.map(e => `- ${e.text}`).join('\n')}`);
  }

  const prompt = `Du hjälper en ${scope === 'company' ? 'VD' : 'affärsområdesansvarig'} på Tikab (konsultbolag inom bygg/fastighet) att skapa en tydlig förväntningsmatris för sina medarbetare.

${sections.join('\n\n')}

Generera en strukturerad förväntningsmatris. Returnera ENBART giltig JSON:
[
  {"id": "1", "text": "Förväntning 1", "source": "${scope === 'company' ? 'company' : 'area'}", "active": true},
  {"id": "2", "text": "Förväntning 2", "source": "${scope === 'company' ? 'company' : 'area'}", "active": true}
]

Max 15 förväntningar, konkreta och mätbara. Inkludera ärvda förväntningar om de finns.`;

  const json = await callClaude({
    model: 'claude-haiku-4-5-20251001',
    max_tokens: 2000,
    messages: [{ role: 'user', content: prompt }]
  });
  const raw = json.content?.[0]?.text || '';
  try { return JSON.parse(raw); } catch {
    const m = raw.match(/\[[\s\S]*\]/);
    if (m) return JSON.parse(m[0]);
    throw new Error('Kunde inte tolka AI-svaret.');
  }
}

// ── Dokumentväljare från DokumentN ────────────────────────────────
function DocumentPicker({ linkedDocIds, onToggle }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'documentLibrary'), where('category', '==', 'Medarbetare'))).then(snap => {
      setDocs(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv')));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress size={20} />;

  return (
    <Box>
      {docs.length === 0 && (
        <Typography variant="body2" color="text.secondary">Inga dokument i DokumentN.</Typography>
      )}
      {docs.map(d => {
        const linked = linkedDocIds.includes(d.id);
        return (
          <FormControlLabel
            key={d.id}
            control={
              <Checkbox
                size="small"
                checked={linked}
                onChange={() => onToggle(d.id, d.currentFileUrl)}
                icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                checkedIcon={<CheckBoxIcon sx={{ fontSize: 18, color: '#FF6B00' }} />}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                <Typography variant="body2">{d.name}</Typography>
                {d.category && <Chip size="small" label={d.category} sx={{ height: 16, fontSize: '0.6rem' }} />}
              </Box>
            }
            sx={{ display: 'flex', mb: 0.5 }}
          />
        );
      })}
    </Box>
  );
}

// ── Förväntningsmatris (visas + redigeras) ────────────────────────
function MatrixEditor({ matrix, onChange }) {
  const [newText, setNewText] = useState('');

  function toggle(id) {
    onChange(matrix.map(m => m.id === id ? { ...m, active: !m.active } : m));
  }
  function remove(id) {
    onChange(matrix.filter(m => m.id !== id));
  }
  function add() {
    if (!newText.trim()) return;
    onChange([...matrix, {
      id: Date.now().toString(), text: newText.trim(), source: 'manual', active: true
    }]);
    setNewText('');
  }

  return (
    <Box>
      <List dense disablePadding>
        {matrix.map(m => (
          <ListItem key={m.id} disableGutters sx={{ py: 0.5 }}>
            <Checkbox size="small" checked={m.active} onChange={() => toggle(m.id)}
              icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
              checkedIcon={<CheckBoxIcon sx={{ fontSize: 18, color: '#FF6B00' }} />}
              sx={{ p: 0.5, mr: 1 }} />
            <ListItemText
              primary={m.text}
              secondary={m.source === 'company' ? 'Bolagsnivå' : m.source === 'area' ? 'Affärsområde' : 'Manuellt'}
              primaryTypographyProps={{ variant: 'body2', sx: { textDecoration: m.active ? 'none' : 'line-through', color: m.active ? 'inherit' : 'text.disabled' } }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
            <ListItemSecondaryAction>
              <Tooltip title="Ta bort">
                <IconButton size="small" onClick={() => remove(m.id)}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      {matrix.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ingen matris genererad ännu. Klicka "Generera matris" ovan.
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField size="small" fullWidth value={newText}
          onChange={e => setNewText(e.target.value)}
          placeholder="Lägg till förväntning manuellt..."
          onKeyDown={e => e.key === 'Enter' && add()} />
        <Button size="small" variant="contained" onClick={add} disabled={!newText.trim()}
          sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, flexShrink: 0 }}>
          <AddIcon sx={{ fontSize: 18 }} />
        </Button>
      </Box>
    </Box>
  );
}

// ── Gemensam editor-vy (VD = company, AO = area) ──────────────────
function ExpectationsEditor({ currentUser, myProfile, inheritedData, scope, docId }) {
  const [personalRole, setPersonalRole] = useState('');
  const [personalGoals, setPersonalGoals] = useState([]);
  const [newGoalText, setNewGoalText] = useState('');
  const [linkedDocIds, setLinkedDocIds] = useState([]);
  const [linkedDocUrls, setLinkedDocUrls] = useState({});
  const [freeText, setFreeText] = useState('');
  const [expectations, setExpectations] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [newExpText, setNewExpText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'iupExpectations', docId)).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        setPersonalRole(d.personalRole || '');
        setPersonalGoals(d.personalGoals || []);
        setLinkedDocIds(d.linkedDocIds || []);
        setLinkedDocUrls(d.linkedDocUrls || {});
        setFreeText(d.freeText || '');
        setExpectations(d.expectations || []);
        setMatrix(d.matrix || []);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [docId]);

  function addGoal() {
    if (!newGoalText.trim()) return;
    setPersonalGoals(prev => [...prev, newGoalText.trim()]);
    setNewGoalText('');
  }

  function toggleDoc(id, url) {
    setLinkedDocIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    setLinkedDocUrls(prev =>
      prev[id] ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id))
        : { ...prev, [id]: url }
    );
  }

  function addExpectation() {
    if (!newExpText.trim()) return;
    setExpectations(prev => [...prev, { id: Date.now().toString(), text: newExpText.trim(), active: true }]);
    setNewExpText('');
  }

  async function handleGenerateMatrix() {
    setGenerating(true);
    setGenError('');
    try {
      const docTexts = await Promise.all(
        linkedDocIds.map(id => linkedDocUrls[id]
          ? fetchDocumentText(linkedDocUrls[id]).catch(() => '')
          : Promise.resolve('')
        )
      );
      const inheritedMatrix = inheritedData?.matrix?.filter(m => m.active) || [];
      const result = await generateMatrix({ docTexts, freeText, expectations, inheritedMatrix, scope });
      setMatrix(result);
    } catch (e) {
      setGenError(e.message || 'Analys misslyckades.');
    }
    setGenerating(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setDoc(doc(db, 'iupExpectations', docId), {
        scope, authorId: currentUser.uid, authorRole: myProfile.role,
        departmentId: myProfile.departmentId || null,
        personalRole, personalGoals,
        linkedDocIds, linkedDocUrls, freeText, expectations, matrix,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  }

  const isCompany = scope === 'company';
  const label = isCompany ? 'för bolaget' : `för ${myProfile.departmentName || 'affärsområdet'}`;
  const roleLabel = isCompany ? 'VD' : 'Affärsområdesansvarig';

  if (loading) return <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />;

  return (
    <Box>
      {/* Egna mål */}
      <Paper sx={{ p: 2.5, mb: 3, borderLeft: '4px solid #FF6B00' }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
          Mina egna mål som {roleLabel}
        </Typography>
        <TextField
          multiline minRows={2} fullWidth size="small"
          label="Min roll och mitt fokus"
          placeholder={`Beskriv din roll som ${roleLabel} och vad du fokuserar på...`}
          value={personalRole}
          onChange={e => setPersonalRole(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Mina mål för perioden
        </Typography>
        {personalGoals.map((g, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>· {g}</Typography>
            <IconButton size="small" onClick={() => setPersonalGoals(prev => prev.filter((_, j) => j !== i))}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField size="small" fullWidth value={newGoalText}
            onChange={e => setNewGoalText(e.target.value)}
            placeholder="Lägg till mål..."
            onKeyDown={e => e.key === 'Enter' && addGoal()} />
          <Button size="small" variant="contained" onClick={addGoal}
            disabled={!newGoalText.trim()}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, flexShrink: 0 }}>
            <AddIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Paper>

      {/* Ärvd matris (read-only) */}
      {inheritedData?.matrix?.length > 0 && (
        <Paper sx={{ p: 2.5, mb: 3, borderLeft: '4px solid #bdbdbd' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Ärvda förväntningar {isCompany ? '' : 'från VD'}
          </Typography>
          <List dense disablePadding>
            {inheritedData.matrix.filter(m => m.active).map(m => (
              <ListItem key={m.id} disableGutters sx={{ py: 0.25 }}>
                <Typography variant="body2" color="text.secondary">· {m.text}</Typography>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Dokument från DokumentN */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ fontSize: 18, color: '#FF6B00' }} />
          Länkade dokument från DokumentN
        </Typography>
        <DocumentPicker linkedDocIds={linkedDocIds} onToggle={toggleDoc} />
      </Paper>

      {/* Fritext */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
          Fritext om förväntningar {label}
        </Typography>
        <TextField multiline minRows={4} fullWidth size="small"
          placeholder="Beskriv generella förväntningar och riktning..."
          value={freeText} onChange={e => setFreeText(e.target.value)} />
      </Paper>

      {/* Strukturerade förväntningar */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
          Förväntningslista
        </Typography>
        <List dense disablePadding sx={{ mb: 1 }}>
          {expectations.map((e, i) => (
            <ListItem key={e.id} disableGutters sx={{ py: 0.25 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{i + 1}. {e.text}</Typography>
              <IconButton size="small" onClick={() => setExpectations(prev => prev.filter(x => x.id !== e.id))}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" fullWidth value={newExpText}
            onChange={ev => setNewExpText(ev.target.value)}
            placeholder="Ny förväntning..."
            onKeyDown={ev => ev.key === 'Enter' && addExpectation()} />
          <Button size="small" variant="contained" onClick={addExpectation}
            disabled={!newExpText.trim()}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, flexShrink: 0 }}>
            <AddIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Paper>

      {/* Generera matris */}
      <Paper sx={{ p: 2.5, mb: 3, borderLeft: '4px solid #FF6B00' }}>
        <Typography variant="subtitle2" fontWeight="bold"
          sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ color: '#FF6B00', fontSize: 18 }} />
          Förväntningsmatris
        </Typography>
        <Button variant="outlined" size="small"
          startIcon={generating ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
          disabled={generating}
          onClick={handleGenerateMatrix}
          sx={{ mb: 1.5, borderColor: '#FF6B00', color: '#FF6B00',
            '&:hover': { borderColor: '#e65c00', bgcolor: '#fff3e0' } }}>
          {generating ? 'Genererar matris…' : 'Generera matris med AI'}
        </Button>
        {genError && <Alert severity="error" sx={{ mb: 1.5 }}>{genError}</Alert>}
        <MatrixEditor matrix={matrix} onChange={setMatrix} />
      </Paper>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
          {saving ? 'Sparar...' : saved ? '✓ Sparat' : 'Spara förväntningar'}
        </Button>
      </Box>
    </Box>
  );
}

// ── Huvud-komponent ───────────────────────────────────────────────
export default function IUPExpectations() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [myProfile, setMyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then(async snap => {
      const profile = snap.exists() ? { id: currentUser.uid, ...snap.data() } : { id: currentUser.uid };
      setMyProfile(profile);

      // AO needs to see the company (VD) data
      if (profile.role === 'ao_ansvarig' || profile.isDepartmentManager) {
        try {
          const vdSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'vd')));
          if (!vdSnap.empty) {
            const vdId = vdSnap.docs[0].id;
            const expSnap = await getDoc(doc(db, 'iupExpectations', `company_${vdId}`));
            if (expSnap.exists()) setCompanyData(expSnap.data());
          }
        } catch (_) {}
      }
      setLoading(false);
    });
  }, [currentUser]);

  const isVD = myProfile?.role === 'vd' || myProfile?.role === 'superadmin';
  const isAO = myProfile?.role === 'ao_ansvarig' || myProfile?.isDepartmentManager;
  const docId = isVD
    ? `company_${currentUser.uid}`
    : `area_${currentUser.uid}`;

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <Typography variant="subtitle1" fontWeight="bold">Förväntningar</Typography>
      </Box>
      <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
    </Box>
  );

  if (!isVD && !isAO) return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Alert severity="info">Denna sida är tillgänglig för VD och affärsområdesansvariga.</Alert>
    </Container>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">Mitt IUP</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {isVD ? 'Mina mål & bolagsövergripande förväntningar' : 'Mina mål & affärsområdets förväntningar'}
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <ExpectationsEditor
          currentUser={currentUser}
          myProfile={myProfile}
          inheritedData={isAO ? companyData : null}
          scope={isVD ? 'company' : 'area'}
          docId={docId}
        />
      </Container>
    </Box>
  );
}
